import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams, useNavigate } from "react-router-dom";
import Navigation from "@/components/Navigation";
import { CommitteeBioCard } from "@/components/CommitteeBioCard";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Landmark, ArrowLeft, Users, FileText, Calendar, Newspaper } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const partyColor = (p) => {
  const c = (p || "").toUpperCase()[0];
  if (c === "D") return "bg-blue-100 text-blue-800 border-blue-200";
  if (c === "R") return "bg-red-100 text-red-800 border-red-200";
  if (c === "I") return "bg-purple-100 text-purple-800 border-purple-200";
  return "bg-gray-100 text-gray-800 border-gray-200";
};

const statusColor = (s) => {
  if (s === "Enacted") return "bg-green-100 text-green-800 border-green-200";
  if (s === "Passed House") return "bg-blue-100 text-blue-800 border-blue-200";
  if (s === "Passed Senate") return "bg-purple-100 text-purple-800 border-purple-200";
  return "bg-gray-100 text-gray-700 border-gray-200";
};

export default function CommitteeDetail() {
  const { committeeName } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);
  const [news, setNews] = useState([]);
  const [newsLoading, setNewsLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setErrored(false);
    axios
      .get(`${API}/committees/${encodeURIComponent(committeeName)}`, { timeout: 120000 })
      .then((r) => setData(r.data))
      .catch((e) => {
        setErrored(true);
        if (e?.response?.status === 404) toast.error("Committee not found");
        else toast.error("Failed to load committee");
      })
      .finally(() => setLoading(false));
  }, [committeeName]);

  // News fires off in parallel with the committee detail fetch, uses the
  // committee name as the search query against the shared news service. The
  // backend already sorts results newest-first.
  useEffect(() => {
    setNewsLoading(true);
    axios
      .get(`${API}/committees/${encodeURIComponent(committeeName)}/news`, { timeout: 30000 })
      .then((r) => setNews(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(() => setNews([]))
      .finally(() => setNewsLoading(false));
  }, [committeeName]);

  return (
    <div className="min-h-screen" style={{ background: "var(--whip-bg)" }}>
      <Navigation />

      <header className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="mb-3 text-gray-600"
            data-testid="committee-back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="flex items-start gap-3">
            <Landmark className="w-9 h-9 text-emerald-600 shrink-0 mt-1" />
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900" data-testid="committee-detail-title">
                {decodeURIComponent(committeeName)}
              </h1>
              {data && (
                <p className="text-sm text-gray-600 mt-1">
                  {data.chamber || "Congress"} · {data.memberCount} members
                </p>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading && (
          <div className="grid lg:grid-cols-3 gap-6" data-testid="committee-loading-skeleton">
            {/* Members skeleton */}
            <Card className="lg:col-span-2 p-6 rounded-xl" style={{ background: "var(--whip-surface)" }}>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-5 h-5 rounded bg-white/10 animate-pulse" />
                <div className="h-5 w-24 bg-white/10 rounded animate-pulse" />
                <div className="ml-auto w-10 h-5 bg-white/10 rounded animate-pulse" />
              </div>
              <div className="flex items-center gap-2 mb-6 text-sm text-orange-300/90">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-orange-400 border-t-transparent" />
                <span>Pulling committee roster and recent bills from Congress.gov…</span>
              </div>
              <div className="space-y-3">
                {Array.from({ length: 10 }).map((_, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-white/5">
                    <div className="h-4 bg-white/10 rounded animate-pulse" style={{ width: `${50 + (i % 4) * 12}%` }} />
                    <div className="h-4 w-20 bg-white/10 rounded animate-pulse" />
                  </div>
                ))}
              </div>
            </Card>
            {/* Bills skeleton */}
            <Card className="p-6 rounded-xl" style={{ background: "var(--whip-surface)" }}>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-5 h-5 rounded bg-white/10 animate-pulse" />
                <div className="h-5 w-28 bg-white/10 rounded animate-pulse" />
              </div>
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="border border-white/5 rounded-lg p-3 space-y-2">
                    <div className="flex gap-2">
                      <div className="h-4 w-14 bg-white/10 rounded animate-pulse" />
                      <div className="h-4 w-20 bg-white/10 rounded animate-pulse" />
                    </div>
                    <div className="h-3 w-full bg-white/10 rounded animate-pulse" />
                    <div className="h-3 w-3/4 bg-white/10 rounded animate-pulse" />
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {!loading && errored && !data && (
          <Card
            className="p-10 text-center rounded-xl border border-rose-500/30"
            style={{ background: "linear-gradient(135deg, rgba(255,59,59,0.08), rgba(11,11,15,0.95))" }}
            data-testid="committee-error-state"
          >
            <div className="text-4xl mb-3">⚠️</div>
            <h3 className="text-xl font-bold text-white mb-2">We couldn't load this committee</h3>
            <p className="text-sm text-white/60 mb-5 max-w-md mx-auto">
              The Congress.gov feed didn't return data for
              {" "}<span className="text-white/80 font-mono">{decodeURIComponent(committeeName)}</span>.
              It may have been renamed, disbanded, or the feed is temporarily unreachable.
            </p>
            <div className="flex items-center justify-center gap-3">
              <Button
                onClick={() => window.location.reload()}
                className="bg-orange-500 hover:bg-orange-600 text-white"
                data-testid="committee-retry-button"
              >
                Retry
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/committees")}
                className="border-white/20 text-white hover:bg-white/5"
                data-testid="committee-back-to-list"
              >
                Back to committees
              </Button>
            </div>
          </Card>
        )}

        {!loading && data && (
          <CommitteeBioCard committeeName={data.name} />
        )}

        {!loading && data && (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Members */}
            <Card className="lg:col-span-2 p-6 bg-white rounded-xl border-2 border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-5 h-5 text-emerald-600" />
                <h2 className="text-xl font-bold text-gray-900">Members</h2>
                <Badge variant="outline" className="ml-auto">{data.members.length}</Badge>
              </div>
              <div className="divide-y divide-gray-100">
                {data.members.map((m, i) => (
                  <div
                    key={(m.legislatorId || m.name) + i}
                    className="flex flex-wrap items-center justify-between gap-2 py-2.5"
                    data-testid={`committee-member-${i}`}
                  >
                    <div className="min-w-0">
                      {m.legislatorId ? (
                        <Link
                          to={`/legislator/${m.legislatorId}`}
                          className="font-medium text-gray-900 hover:text-emerald-700"
                        >
                          {m.name}
                        </Link>
                      ) : (
                        <span className="font-medium text-gray-900">{m.name}</span>
                      )}
                      {m.title && m.title !== "Member" && (
                        <span className="ml-2 text-xs font-semibold uppercase tracking-wide text-emerald-700">
                          {m.title}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`${partyColor(m.party)} border px-2 py-0.5 text-xs`}>
                        {m.party || "?"}
                      </Badge>
                      <span className="text-sm text-gray-600">{m.state}</span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Recent bills */}
            <Card className="p-6 bg-white rounded-xl border-2 border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="w-5 h-5 text-blue-600" />
                <h2 className="text-xl font-bold text-gray-900">Recent Bills</h2>
              </div>
              <p className="text-xs text-gray-500 mb-3">
                {data.billsSource === "referred" ? "The most recent bills referred to or reported by this committee." : "The most recent bills sponsored by members of this committee."}
                {data.recentBills.length > 0 && ` Showing ${data.recentBills.length}, scroll for more.`}
              </p>
              {data.recentBills.length === 0 && (
                <p className="text-sm text-gray-500">No recent bills surfaced.</p>
              )}
              <ul className="space-y-3 max-h-[560px] overflow-y-auto pr-1" data-testid="committee-recent-bills-scroll">
                {data.recentBills.map((b) => (
                  <li key={b.id} className="border border-gray-100 rounded-lg p-3 hover:border-blue-300 transition-colors">
                    <Link to={`/bill/${b.id}`} className="block" data-testid={`committee-bill-${b.id}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200 border text-xs px-2 py-0.5">
                          {b.billNumber}
                        </Badge>
                        <Badge className={`${statusColor(b.status)} border text-xs px-2 py-0.5`}>
                          {b.status}
                        </Badge>
                      </div>
                      <p className="text-sm font-medium text-gray-900 line-clamp-2">{b.title}</p>
                      {b.introducedDate && (
                        <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(b.introducedDate).toLocaleDateString()}
                        </p>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            </Card>

            {/* In the news, pulled from the shared news service. Already
                sorted newest-first by the backend. */}
            <Card className="lg:col-span-2 p-6 bg-white rounded-xl border-2 border-gray-200" data-testid="committee-news-card">
              <div className="flex items-center gap-2 mb-4">
                <Newspaper className="w-5 h-5 text-orange-600" />
                <h2 className="text-xl font-bold text-gray-900">In the News</h2>
                {news.length > 0 && (
                  <span className="ml-auto text-xs text-gray-500" data-testid="committee-news-count">
                    {news.length}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 mb-3">
                Recent press coverage mentioning this committee.
              </p>
              {newsLoading ? (
                <p className="text-sm text-gray-500">Loading news…</p>
              ) : news.length === 0 ? (
                <p className="text-sm text-gray-500">No recent news found.</p>
              ) : (
                <ul className="space-y-3 max-h-96 overflow-y-auto pr-1">
                  {news.map((n, i) => (
                    <li
                      key={i}
                      className="border border-gray-100 rounded-lg p-3 hover:border-orange-300 transition-colors"
                      data-testid={`committee-news-item-${i}`}
                    >
                      <a href={n.link} target="_blank" rel="noopener noreferrer" className="block">
                        <p className="text-sm font-medium text-gray-900 line-clamp-2 hover:text-orange-700">
                          {n.title}
                        </p>
                        <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                          <span>{n.source}</span>
                          {n.publishedAt && (
                            <>
                              <span>·</span>
                              <span>{new Date(n.publishedAt).toLocaleDateString()}</span>
                            </>
                          )}
                        </div>
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}
